import { Player } from '@lottiefiles/react-lottie-player';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Button from '../../button/button';
import Textfield from '../../textfield/textfield';
import styles from './../alert.module.scss';

import { ALERT, USER } from '@flexo/reducers';
import parse from 'html-react-parser';

const loader = '/loaderLottie.json';

interface AlertConfirmCredentialsChangesProps {
  type?: 'credentials' | 'delete';
}

export default function AlertConfirmCredentialsChanges({
  type = 'credentials',
}: AlertConfirmCredentialsChangesProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const alertState = useSelector((state: any) => state.alert);
  const user = useSelector((state: any) => state.user);

  const translationKey =
    type === 'delete'
      ? 'banners.confirm_delete_account'
      : 'banners.confirm_credentials_changes';
  const alertContent = t(translationKey, {
    returnObjects: true,
  }) as any;

  const requirePassword =
    type === 'delete' ? true : Boolean(alertState?.alert?.requirePassword);

  const [passwordField, setPasswordField] = useState('');
  const [fieldVisible, setFieldVisible] = useState(false);
  const [formError, setFormError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const currentEmail =
    user?.email ||
    user?.user?.email ||
    user?.tokenContent?.firebaseUserResult?.email ||
    '';

  const resetAlert = useCallback(
    () => dispatch(ALERT.ResetAlert({} as any)),
    [dispatch]
  );

  const handleCancel = () => {
    alertState?.alert?.dismissCallback?.();
    resetAlert();
  };

  const handleForgotPassword = () => {
    alertState?.alert?.dismissCallback?.();
    resetAlert();
    localStorage.setItem('redirectToForgotPassword', 'true');
    dispatch(USER.ShowSplashScreen());
    dispatch(USER.SetIsLoggingOut());
    dispatch(USER.SetLogout());
  };

  const handleConfirm = useCallback(async () => {
    if (isSubmitting) return;

    // FLEXO-6112 (#8): this dialog re-authenticates with the EXISTING password;
    // it must not re-apply the registration strength policy (special-char etc.),
    // which was stricter than what registration enforced and permanently locked
    // out users whose real password had no special character. Only require that a
    // password was entered — Clerk rejects it if it's wrong.
    if (requirePassword && !passwordField) {
      setFormError(true);
      return;
    }

    setIsSubmitting(true);
    try {
      const result = alertState?.alert?.callback
        ? await alertState.alert.callback(
            requirePassword ? passwordField : undefined
          )
        : 200;

      if (typeof result === 'number' && result > 200) {
        setFormError(true);
        if (requirePassword) {
          setPasswordField('');
        }
        return;
      }

      alertState?.alert?.dismissCallback?.();
      resetAlert();
    } finally {
      setIsSubmitting(false);
    }
  }, [
    alertState?.alert,
    isSubmitting,
    passwordField,
    requirePassword,
    resetAlert,
  ]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordField(event.target.value);
    if (formError) {
      setFormError(false);
    }
  };

  useEffect(() => {
    if (!requirePassword) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Enter' && !isSubmitting) {
        event.preventDefault();
        event.stopPropagation();
        handleConfirm();
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [requirePassword, isSubmitting, handleConfirm]);

  return (
    <div>
      <div
        id="alert-confirm-credentials-changes"
        className={`${styles.Alert} ${styles.Alert__Confirm__Credentials__Changes}`}
      >
        <div className={styles.Alert__Title}>{alertContent.title}</div>
        <div className={styles.Alert__Content}>
          {parse(alertContent.description)}
        </div>
        {requirePassword && (
          <>
            <div className={`heading4 ${styles.Alert__Content__Label} mt-1`}>
              {alertContent.inputLabel}
            </div>
            <div className={styles.Alert__Content__Content}>
              <span
                className={styles.Alert__Content__Link}
                onClick={handleForgotPassword}
              >
                {alertContent.forgotPassword}
              </span>
              <span> {alertContent.inputSubtitle}</span>
            </div>

            <div
              className={styles.Alert__Content__Form}
              onClick={(event) => {
                const parent = event.currentTarget;
                const child = parent.querySelector(
                  'input[name="reauth-password"]'
                );
                child?.focus();
              }}
            >
              <input
                type="email"
                name="username"
                autoComplete="username"
                value={currentEmail}
                readOnly
                style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
                tabIndex={-1}
              />
              <Textfield
                label=""
                name="reauth-password"
                type={fieldVisible ? 'text' : 'password'}
                placeholder={alertContent.inputPlaceholder}
                onPaste={(e) => false}
                onCopy={(e) => false}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                autoComplete="current-password"
                value={passwordField}
                onChange={handleInputChange}
                icon={fieldVisible ? 'show' : 'hide'}
                onIconClick={() => setFieldVisible((prev) => !prev)}
                error={formError}
                errorDescription={formError ? alertContent.error : ''}
                color="white"
              />
            </div>
          </>
        )}

        <div className={styles.Alert__Footer__Actions}>
          <Button
            variant="secondary"
            color="grey"
            className={`${styles.Alert__Footer__Actions__Button} ${styles.Alert__Footer__Actions__Button__Cancel}`}
            label={alertContent.cancel}
            onClick={handleCancel}
          />

          <Button
            className={`${styles.Alert__Footer__Actions__Button} ${styles.Alert__Footer__Actions__Button__Confirm}`}
            variant="primary"
            color={type === 'delete' ? 'negative' : 'secondary'}
            label={
              isSubmitting ? (
                <Player
                  autoplay
                  loop
                  src={loader}
                  style={{ height: 20, width: 60 }}
                />
              ) : (
                alertContent.confirm
              )
            }
            disabled={
              isSubmitting ||
              (requirePassword && !passwordField)
            }
            onClick={handleConfirm}
          />
        </div>
      </div>
    </div>
  );
}
